import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { Loader2, Lock, Save } from "lucide-react";
import { toast } from "sonner";

import { SET_PARENT_PIN } from "@/graphql/mutations/kid-mode";
import { SAVE_SETTINGS } from "@/graphql/mutations/admin";
import { ME_QUERY, useAuth } from "@/lib/auth/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * The parent PIN and the switch that decides whether handing the device to a
 * learner is offered at all.
 *
 * The PIN is what gets a grown-up back out of the kid shell. Without one set,
 * switching in is refused outright rather than stranding somebody in a child's
 * account.
 */
export function KidModeSettings({
  impersonationEnabled,
  onImpersonationChange,
}: {
  impersonationEnabled: boolean;
  onImpersonationChange?: (value: boolean) => void;
}) {
  const { isAdmin, parentPinSet } = useAuth();
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [currentPin, setCurrentPin] = useState("");

  const [setParentPin, { loading: savingPin }] = useMutation(SET_PARENT_PIN, {
    refetchQueries: [ME_QUERY],
  });
  const [saveSettings, { loading: savingToggle }] = useMutation(SAVE_SETTINGS, {
    refetchQueries: [ME_QUERY],
  });

  const savePin = async () => {
    if (pin !== confirmPin) {
      toast.error("PIN kodai nesutampa");
      return;
    }

    try {
      const { data } = await setParentPin({
        variables: { pin, currentPin: parentPinSet ? currentPin : null },
      });
      const result = (data as any)?.setParentPin;

      if (result?.success) {
        toast.success(parentPinSet ? "PIN pakeistas" : "PIN nustatytas");
        setPin("");
        setConfirmPin("");
        setCurrentPin("");
      } else {
        toast.error(result?.error ?? "Nepavyko išsaugoti PIN");
      }
    } catch {
      toast.error("Nepavyko išsaugoti PIN");
    }
  };

  const toggleImpersonation = async (value: boolean) => {
    try {
      await saveSettings({ variables: { impersonationEnabled: value } });
      onImpersonationChange?.(value);
      toast.success(value ? "Perjungimas įjungtas" : "Perjungimas išjungtas");
    } catch {
      toast.error("Nepavyko išsaugoti");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-4 w-4" />
          Vaiko režimas
        </CardTitle>
        <CardDescription>
          Perjungus įrenginį vaikui, atgal grįžtama tik su šiuo PIN kodu. Jis
          galioja tik jūsų šeimai.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {!parentPinSet && (
          <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
            PIN kodas dar nenustatytas — kol jo nėra, perjungti į vaiko paskyrą
            negalima.
          </p>
        )}

        <div className="grid gap-3 sm:max-w-sm">
          {parentPinSet && (
            <div className="space-y-1.5">
              <Label htmlFor="current-pin">Esamas PIN</Label>
              <Input
                id="current-pin"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                value={currentPin}
                onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ""))}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="new-pin">{parentPinSet ? "Naujas PIN" : "PIN"}</Label>
            <Input
              id="new-pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              placeholder="4–8 skaitmenys"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirm-pin">Pakartokite</Label>
            <Input
              id="confirm-pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
            />
          </div>

          <Button
            className="w-fit"
            disabled={savingPin || pin.length < 4 || (parentPinSet && currentPin.length < 4)}
            onClick={savePin}
          >
            {savingPin ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {parentPinSet ? "Pakeisti PIN" : "Nustatyti PIN"}
          </Button>
        </div>

        {/* Turning this off suits a teacher/student setup, where a grown-up
            should never be acting inside a learner's account. */}
        {isAdmin && (
          <div className="flex items-start gap-3 border-t pt-4">
            <Checkbox
              id="impersonation"
              checked={impersonationEnabled}
              disabled={savingToggle}
              onCheckedChange={(value) => toggleImpersonation(value === true)}
            />
            <div className="space-y-1">
              <Label htmlFor="impersonation" className="font-medium">
                Leisti perjungti į mokinio paskyrą
              </Label>
              <p className="text-sm text-muted-foreground">
                Išjunkite, jei mokiniai jungiasi su savo paskyromis ir globėjui
                nereikėtų veikti jų vardu.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
